import React, { useState, useEffect, useCallback, useRef } from 'react';
import APIService from '../services/api';
import { isAoAdmin } from './AoSociete';
import { ancre } from '../tutoriel/ancres';

/**
 * Réglages de l'algorithme de matching — le chantier « transparence ».
 *
 * Le reproche utilisateur n'est pas « le score est faux », c'est « je ne peux
 * pas le défendre » : un chatbot annonce 85 là où erpia dit 60, et personne ne
 * sait expliquer l'écart. La réponse tient en trois pièces, toutes ici :
 *
 *  1. le CATALOGUE des critères et leurs poids, publiés par le serveur — le
 *     front n'invente aucun critère : un critère absent du catalogue n'existe
 *     pas ;
 *  2. les mêmes curseurs à deux endroits : la configuration (défaut de
 *     l'organisation) et le POPUP de lancement (ajustement pour CE matching
 *     uniquement, sans toucher au défaut) ;
 *  3. le DÉTAIL d'un score : critère par critère, poids × note = contribution,
 *     avec une explication lisible — c'est ce qui permet de répondre à
 *     « pourquoi 60 et pas 85 ? ».
 *
 * Toutes les routes sont tolérées absentes (404/405/501 → « arrive côté
 * serveur », rien n'est écrit).
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)',
  fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
};
const btnPlein = { ...btnGhost, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' };

/**
 * GET /config/matching → { criteres: [{cle, libelle, description, poids,
 * poids_defaut, min, max, part_pct}], niveaux: [{niveau, libelle, effet}] }.
 * `poids` porte un NIVEAU de 0 à 5 — la MÊME échelle que celle des exigences
 * d'un besoin (demande 21.09 : « que ça devienne pareil, un comptage de 0 à
 * 5 »). Les libellés viennent du serveur, cet écran n'en écrit aucun.
 * Renvoie { criteres, niveaux, absent, erreur } — jamais de
 * throw : l'appelant affiche, il ne plante pas.
 */
export async function chargerCatalogueMatching() {
  try {
    const r = await fetch(`${BASE}/config/matching`, { headers: authH() });
    if (r.status === 404 || r.status === 405 || r.status === 501) return { criteres: null, absent: true };
    const j = await r.json().catch(() => ({}));
    if (!r.ok || j.success === false) return { criteres: null, erreur: j.error || `HTTP ${r.status}` };
    const bruts = j.criteres || j.data?.criteres || [];
    // Les poids peuvent aussi arriver dans une table à part, à la racine.
    const table = j.poids || j.valeurs || j.data?.poids || null;
    // Normalisation : plusieurs écritures serveur tolérées, et surtout JAMAIS
    // de 0 inventé — un poids que le serveur ne donne pas reste null (affiché
    // « — », non enregistrable), plutôt qu'un zéro qui casserait les scores.
    const criteres = (Array.isArray(bruts) ? bruts : []).map(c => {
      const defBrut = c.poids_defaut ?? c.defaut ?? c.poids_initial ?? null;
      const curBrut = c.poids ?? c.poids_actuel ?? c.poids_courant ?? c.valeur ?? c.weight ?? table?.[c.cle] ?? defBrut;
      const num = (x) => (x == null || x === '' || Number.isNaN(Number(x))) ? null : Number(x);
      const cur = num(curBrut);
      const def = num(defBrut);
      return { ...c, poids: cur, poids_defaut: def ?? cur };
    });
    // L'ÉCHELLE, SERVIE : un serveur d'avant ne la publie pas — les curseurs
    // restent alors des nombres nus, comme avant. Rien n'est inventé.
    const niveaux = Array.isArray(j.niveaux) && j.niveaux.length ? j.niveaux : null;
    return { criteres, niveaux, valeursConnues: criteres.length > 0 && criteres.every(c => c.poids != null) };
  } catch (e) { return { criteres: null, erreur: e.message }; }
}

const poidsDe = (criteres) => Object.fromEntries((criteres || []).map(c => [c.cle, c.poids]));
const poidsDefautDe = (criteres) => Object.fromEntries((criteres || []).map(c => [c.cle, c.poids_defaut]));

/**
 * Les curseurs, un par critère du catalogue. La part (%) est recalculée en
 * direct : « donner plus de poids au diplôme », c'est voir sa part grossir.
 */
export function PoidsSliders({ criteres, poids, onChange, disabled = false, niveaux = null }) {
  // Le nom du cran, servi par le serveur — le même vocabulaire que sur une
  // exigence : 5 Éliminatoire … 0 Ne pas compter.
  const nomNiveau = (n) => (niveaux || []).find(x => x.niveau === Number(n))?.libelle || null;
  const effetNiveau = (n) => (niveaux || []).find(x => x.niveau === Number(n))?.effet || null;
  const total = Object.values(poids).reduce((s, v) => s + (Number(v) || 0), 0);
  // Le poids d'avant une mise à zéro : « Recompter » remet ce que l'on avait,
  // pas une valeur inventée.
  const repris = useRef({});
  useEffect(() => {
    for (const [k, v] of Object.entries(poids || {})) if (Number(v) > 0) repris.current[k] = Number(v);
  }, [poids]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {(criteres || []).map(c => {
        // null = le serveur n'a pas donné ce poids : « — », curseur inerte —
        // jamais un 0 inventé qu'on pourrait enregistrer par mégarde.
        const inconnu = poids[c.cle] == null;
        const v = Number(poids[c.cle] ?? 0);
        const ignore = !inconnu && v === 0;
        const part = !inconnu && total > 0 ? Math.round((v / total) * 100) : null;
        return (
          <div key={c.cle} style={{ display: 'flex', alignItems: 'center', gap: '12px', opacity: disabled ? 0.75 : 1 }}>
            <div style={{ flex: '0 0 200px', minWidth: 0 }}>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{c.libelle || c.cle}</div>
              {c.description && <div style={{ fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.4 }}>{c.description}</div>}
            </div>
            <input type="range" min={c.min ?? 0} max={c.max ?? 100} step={1} value={v} disabled={disabled || inconnu || ignore}
              onChange={e => onChange({ ...poids, [c.cle]: Number(e.target.value) })}
              title={effetNiveau(v) || undefined}
              style={{ flex: 1, accentColor: 'var(--brand)', opacity: ignore ? 0.4 : 1 }} />
            {/* LE CRAN, PUIS SA PART. Le nom du cran (« Important ») dit ce
                qu'on règle ; le % dit ce que ça pèse vraiment une fois les
                quatre catégories mises ensemble — c'est lui qui empêche de
                tout monter à 5. */}
            <span style={{ flex: 'none', fontSize: '12px', fontWeight: 600, color: ignore ? 'var(--text-faint)' : 'var(--text-2)', width: niveaux ? '150px' : '84px', textAlign: 'right', lineHeight: 1.3 }}>
              {inconnu ? '—' : ignore ? (nomNiveau(0) || 'ignoré') : (
                <>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{v}</span>
                  {nomNiveau(v) && <span style={{ color: 'var(--text-strong)' }}> {nomNiveau(v)}</span>}
                  <span style={{ color: 'var(--text-faint)', fontFamily: "'IBM Plex Mono', monospace" }}> · {part}%</span>
                </>
              )}
            </span>
            {/* NE PAS COMPTER : le curseur descendait déjà à 0, mais personne
                ne pouvait deviner que 0 neutralise la famille. Vécu 16.09 : un
                architecte à 60/100 sur un mandat IA, dont 15 points perdus sur
                des critères « optionnels » (un passé aéronautique) que le
                client ne demandait pas vraiment. Le geste est maintenant nommé.
                Le poids d'avant est mémorisé : rallumer le remet tel quel. */}
            <button type="button" disabled={disabled || inconnu}
              onClick={() => onChange({ ...poids, [c.cle]: ignore ? (repris.current[c.cle] || c.poids_defaut || 3) : 0 })}
              title={ignore
                ? 'Recompter ce critère dans le score.'
                : 'Ne plus compter ce critère : il ne pèsera ni en faveur ni contre les candidats, et son poids ira aux autres. Un candidat ne sera plus pénalisé pour un atout que vous ne cherchez pas.'}
              style={{ flex: 'none', padding: '3px 9px', borderRadius: '7px', fontSize: '11px', fontWeight: 600, cursor: (disabled || inconnu) ? 'not-allowed' : 'pointer',
                border: `1px solid ${ignore ? 'var(--brand)' : 'var(--border)'}`, background: 'transparent', color: ignore ? 'var(--brand)' : 'var(--text-3)', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              {ignore ? 'Recompter' : 'Ne pas compter'}
            </button>
          </div>
        );
      })}
      {total > 0 && !disabled && (criteres || []).some(c => poids[c.cle] === 0) && (
        <div style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>
          Un critère « ignoré » ne compte ni en faveur ni contre : son poids va aux autres, et le détail d’un score le dit.
        </div>
      )}
      {total === 0 && !disabled && (criteres || []).every(c => poids[c.cle] != null) && (
        <div style={{ fontSize: '11.5px', color: '#b45309' }}>
          Toutes les catégories sont à zéro : rien ne compterait. Remontez au moins un curseur.
        </div>
      )}
      {niveaux && !disabled && (
        <div style={{ fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.5 }}>
          Même échelle que les exigences ci-dessus : de <strong>{nomNiveau(0)}</strong> à <strong>{nomNiveau(5)}</strong>.
          Seuls les rapports comptent — c'est la part en % qui dit ce que chaque catégorie pèse vraiment.
        </div>
      )}
    </div>
  );
}

/**
 * LES EXIGENCES DU BESOIN, VISIBLES ET CORRIGEABLES.
 *
 * Demande 15.09 : « le souci c'est que chaque client a son format de JD ».
 * Le découpage d'une fiche de poste se fait par l'IA, qui lit le sens et pas
 * la mise en page — mais aucune lecture n'est juste à tous les coups sur tous
 * les formats. Ce qui manquait n'était pas un meilleur détecteur : les
 * curseurs de poids réglaient quatre catégories DONT ON NE VOYAIT PAS LE
 * CONTENU. On pondérait des boîtes fermées.
 *
 * Ici, la liste. Chaque exigence se déplace d'une catégorie à l'autre, et la
 * correction survit aux relectures automatiques de la fiche de poste.
 */
/* LA PART RÉELLE D'UNE EXIGENCE DANS LE SCORE. Le serveur normalise la
   moyenne de chaque catégorie (seuls les RAPPORTS entre niveaux comptent),
   puis pondère les catégories entre elles. On rejoue la même règle ici, avec
   les poids de catégorie de la modale — ceux de l'organisation, ou ceux
   qu'on est en train d'ajuster — pour que tourner un curseur ou changer un
   niveau se VOIE sur chaque ligne. Sans ça, tout le monde met tout à 5 et on
   retombe sur l'égalité qu'on voulait fuir.
   Le poids d'un niveau vient de la formule serveur (5 → 1, 4 → 0,8 … 0 → 0).
   Un poids de catégorie que le serveur n'a pas publié reste inconnu : aucune
   part n'est affichée — on n'invente pas un pourcentage. */
const POIDS_NIVEAU = { 5: 1, 4: 0.8, 3: 0.6, 2: 0.4, 1: 0.2, 0: 0 };
const poidsNiveau = (n) => POIDS_NIVEAU[Number(n)] ?? null;
function partsReelles(buckets, cats, poidsCat) {
  if (!poidsCat || !buckets) return null;
  // Comme le serveur : une catégorie sans exigence qui compte ne pèse rien,
  // son poids est redistribué sur les autres.
  const actives = cats.filter(c => c.compte !== false && (buckets[c.cle] || []).some(x => poidsNiveau(x.niveau) > 0));
  if (actives.some(c => poidsCat[c.cle] == null)) return null;
  const total = actives.reduce((s, c) => s + (Number(poidsCat[c.cle]) || 0), 0);
  if (!total) return null;
  const parCat = {}, parLigne = {};
  for (const c of cats) {
    const lignes = buckets[c.cle] || [];
    const partCat = actives.includes(c) ? (Number(poidsCat[c.cle]) || 0) / total : 0;
    parCat[c.cle] = partCat;
    const somme = lignes.reduce((a, x) => a + (poidsNiveau(x.niveau) || 0), 0);
    for (const x of lignes) parLigne[`${c.cle}|${x.label}`] = somme ? partCat * ((poidsNiveau(x.niveau) || 0) / somme) : 0;
  }
  return { parCat, parLigne };
}
const pct = (v) => `${(v * 100).toLocaleString('fr-CH', { maximumFractionDigits: 1 })}\u00a0%`;

function ExigencesBesoin({ besoinId, onChange, poidsCat = null }) {
  const [etat, setEtat] = useState(null);   // null = chargement | { data } | { absent } | { erreur }
  const [busy, setBusy] = useState(null);   // libellé en cours de déplacement

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/criteres`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setEtat({ absent: true }); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setEtat({ erreur: j.error || `HTTP ${r.status}` }); return; }
      setEtat({ data: j });
    } catch (e) { setEtat({ erreur: e.message }); }
  }, [besoinId]);
  useEffect(() => { charger(); }, [charger]);

  // LES COMPÉTENCES VOISINES (28.10 : « 80 % de Databricks est dans Fabric ») :
  // ce que Leman propose et ce que l'équipe a tranché, exigence par exigence.
  // Servies par le serveur (GET /competences-voisines/besoin/:id) ; absentes
  // d'un serveur d'avant, la liste reste ce qu'elle était.
  const [voisines, setVoisines] = useState(null);  // null = lecture | { parLabel, en_analyse } | { absent }
  const [voisineBusy, setVoisineBusy] = useState(null);
  const chargerVoisines = useCallback(async (attendre = false) => {
    try {
      const r = await fetch(`${BASE}/competences-voisines/besoin/${besoinId}${attendre ? '?attendre=1' : ''}`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setVoisines({ absent: true }); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setVoisines({ absent: true }); return; }
      const parLabel = {};
      for (const e of (j.exigences || [])) parLabel[e.label] = e.voisines || [];
      setVoisines({ parLabel, en_analyse: j.en_analyse === true });
    } catch { setVoisines({ absent: true }); }
  }, [besoinId]);
  useEffect(() => { chargerVoisines(true); }, [chargerVoisines]);
  // ✓ / ✗ pour CE besoin ; « pour tous les besoins » confirme la proposition
  // de Leman dans l'organisation. Le message du serveur, verbatim.
  const trancherVoisine = async (v, geste) => {
    setVoisineBusy(`${v.competence_cle}|${v.voisine_cle}`);
    try {
      const r = geste === 'partout'
        ? await fetch(`${BASE}/competences-voisines`, { method: 'PUT', headers: { ...authH(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ competence: v.competence, voisine: v.voisine, statut: 'confirmee' }) })
        : await fetch(`${BASE}/competences-voisines/besoin/${besoinId}`, { method: 'PUT', headers: { ...authH(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ competence_cle: v.competence_cle, voisine_cle: v.voisine_cle, decision: geste }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      await chargerVoisines(false);
      // Le bandeau « le barème a changé depuis » se relit : une voisine
      // tranchée périme les notes déjà calculées.
      await charger();
    } catch (e) { setEtat(x => ({ ...x, erreur: e.message })); }
    finally { setVoisineBusy(null); }
  };

  // Un seul geste serveur pour les deux réglages : la catégorie (`bucket`)
  // et le niveau d'importance (`niveau`). Ce qu'on n'envoie pas ne bouge
  // pas ; `bucket: null` seul remet tout comme la machine l'a lu.
  const deplacer = async (label, mouvement) => {
    setBusy(label);
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/criteres`, {
        method: 'PUT', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ mouvements: [{ label, ...mouvement }] }),
      });
      const j = await r.json().catch(() => ({}));
      // Le message du serveur, verbatim : c'est lui qui dit ce qui se corrige.
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setEtat({ data: j });
      onChange?.(j);
    } catch (e) { setEtat(x => ({ ...x, erreur: e.message })); }
    finally { setBusy(null); }
  };

  if (etat == null) return <div style={{ fontSize: '12px', color: 'var(--text-faint)', padding: '8px 0' }}>Lecture de la fiche de poste…</div>;
  if (etat.absent) return (
    <div style={{ padding: '10px 13px', borderRadius: '10px', background: 'var(--chip-bg)', fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
      La liste des exigences arrive côté serveur — les curseurs ci-dessous restent utilisables.
    </div>
  );
  const d = etat.data;
  // OÙ EN EST LE SCORING (demande 21.09 : « quand je clique sur Scorer, il ne
  // vérifie pas si la JD a changé »). La phrase vient du serveur entière —
  // l'écran ne recompte rien. Absente d'un serveur d'avant : la modale est
  // exactement ce qu'elle était.
  const etatScoring = d?.scoring || null;
  // DEUX choses périment une note : la fiche de poste, et le barème corrigé
  // ici même. La seconde se voit dans cette modale au moment où on la
  // provoque — déplacer une exigence doit alerter tout de suite, pas au
  // prochain classement mélangé.
  const aRefaire = !!etatScoring && (etatScoring.jd_modifiee_depuis || etatScoring.bareme_modifie_depuis);
  const bandeau = etatScoring && (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '9px 12px', borderRadius: '10px', marginBottom: '9px',
      border: `1px solid ${aRefaire ? 'var(--brand)' : 'var(--hair)'}`,
      background: aRefaire ? 'var(--erp-accent-soft)' : 'transparent' }}>
      <BI icon={aRefaire ? 'exclamation-triangle' : etatScoring.deja_score ? 'check2' : 'circle'}
        style={{ flex: 'none', marginTop: '1px', color: aRefaire ? 'var(--brand)' : 'var(--text-3)' }} />
      <span style={{ fontSize: '11.5px', lineHeight: 1.5, color: 'var(--text-2)' }}>
        <strong style={{ color: 'var(--text-strong)' }}>{etatScoring.libelle}</strong>
        {etatScoring.quoi_faire && <> {etatScoring.quoi_faire}</>}
      </span>
    </div>
  );
  if (!d?.criteria?.buckets) return (
    <>
      {bandeau}
      <div style={{ padding: '10px 13px', borderRadius: '10px', background: 'var(--chip-bg)', fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
        Aucune exigence n'a encore été lue sur cette fiche de poste. Le premier matching la lira.
      </div>
    </>
  );
  const cats = d.categories || [];
  const manuel = /manuel/.test(d.source || '');
  const local = /local/.test(d.source || '');
  // L'ÉCHELLE DES NIVEAUX, SERVIE. Un serveur d'avant ne la publie pas : la
  // liste reste alors exactement ce qu'elle était — catégories seules, sans
  // niveau ni part. Rien n'est inventé.
  const niveaux = Array.isArray(d.niveaux) && d.niveaux.length ? d.niveaux : null;
  const plafonne = niveaux?.find(n => n.plafonne);
  const retire = niveaux?.find(n => n.compte === false);
  const parts = niveaux ? partsReelles(d.criteria.buckets, cats, poidsCat) : null;

  return (
    <div {...ancre('matching-exigences')} style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
      {bandeau}
      {/* LA PROVENANCE : « lu par la machine », « lecture sommaire » et
          « corrigé à la main » ne se discutent pas de la même façon quand on
          conteste un score. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-3)' }}>
        <BI icon={manuel ? 'pencil-square' : local ? 'exclamation-triangle' : 'stars'} />
        {manuel ? 'Lecture automatique, corrigée à la main.' : local ? 'Lecture sommaire (la fiche de poste n\'a pas encore été analysée) — barème indicatif.' : 'Lu automatiquement dans la fiche de poste.'}
      </div>
      {/* Ce que le niveau change, dit une fois — les libellés et les effets
          viennent du serveur, seule la phrase est écrite ici. */}
      {niveaux && (
        <div style={{ fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.5 }}>
          Le niveau règle le poids d'une exigence parmi celles de sa catégorie
          {plafonne ? <> — le <strong>{plafonne.niveau}</strong> plafonne le score d'un candidat qui ne la démontre pas</> : null}
          {retire ? <>, le <strong>{retire.niveau}</strong> la retire du calcul</> : null}.
          {parts ? ' La part de chaque ligne dans le score se recalcule à chaque réglage.' : ''}
        </div>
      )}
      {voisines == null && (
        <div style={{ fontSize: '11px', color: 'var(--text-3)' }}><BI icon="arrow-repeat" /> Leman cherche les compétences voisines des exigences…</div>
      )}
      {voisines?.en_analyse && (
        <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Leman lit encore certaines exigences : leurs compétences voisines apparaîtront à la prochaine ouverture.</div>
      )}
      {(d.avertissements || []).map((a, i) => (
        <div key={i} style={{ fontSize: '11px', color: 'var(--text-2)', background: 'var(--chip-bg)', padding: '6px 10px', borderRadius: '8px' }}>{a}</div>
      ))}
      {etat.erreur && <div style={{ fontSize: '11.5px', color: 'var(--danger-fg)' }}>{etat.erreur}</div>}

      {cats.map(cat => {
        const lignes = d.criteria.buckets[cat.cle] || [];
        // « Ne pas compter » : une catégorie qui ne pèse rien. Elle ne
        // s'affiche que si elle contient quelque chose — une corbeille vide
        // n'a rien à dire, et le menu de chaque ligne suffit à y envoyer une
        // exigence. Elle s'estompe : ce n'est pas une liste de travail.
        const horsCalcul = cat.compte === false;
        if (horsCalcul && lignes.length === 0) return null;
        return (
          <div key={cat.cle} style={horsCalcul ? { opacity: 0.6, borderTop: '1px solid var(--hair)', paddingTop: '8px', marginTop: '2px' } : undefined}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.02em', color: 'var(--text-strong)' }}>{cat.libelle}</span>
              {parts && !horsCalcul && (
                <span title="La part de cette catégorie dans le score, avec les poids en vigueur"
                  style={{ fontSize: '10.5px', fontWeight: 600, fontFamily: 'var(--font-mono, inherit)', color: 'var(--text-2)' }}>{pct(parts.parCat[cat.cle] || 0)}</span>
              )}
              <span style={{ fontSize: '10.5px', color: 'var(--text-faint)' }}>{cat.effet}</span>
            </div>
            {lignes.length === 0
              ? <div style={{ fontSize: '11.5px', color: 'var(--text-faint)', paddingBottom: '4px' }}>—</div>
              : lignes.map((x, i) => (
                <div key={x.id ?? i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '5px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '12px', color: 'var(--text)', lineHeight: 1.45 }}>
                    {x.label}
                    {x.corrige && <span title="Déplacée à la main — la correction survit aux relectures de la fiche de poste"
                      style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '5px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)' }}>corrigée</span>}
                    {x.niveau_regle && <span title="Niveau réglé à la main — il survit aux relectures de la fiche de poste"
                      style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '5px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)' }}>niveau réglé</span>}
                    {/* Compte aussi : les voisines de cette exigence. */}
                    {voisines?.parLabel?.[x.label]?.length > 0 && (
                      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
                        <span style={{ fontSize: '10.5px', color: 'var(--text-3)' }} title="Des outils dont la pratique se transfère à cette exigence : un profil qui les a remonte, toujours derrière celui qui a l'exigence elle-même">
                          <BI icon="arrow-left-right" /> Compte aussi
                        </span>
                        {voisines.parLabel[x.label].map(v => {
                          const cle = `${v.competence_cle}|${v.voisine_cle}`;
                          const occupe = voisineBusy === cle;
                          return (
                            <span key={cle} className="erp-chip" style={{ opacity: v.actif ? 1 : 0.55 }}
                              title={[v.etat_libelle, v.raison].filter(Boolean).join(' — ')}>
                              <span style={{ textDecoration: v.actif ? 'none' : 'line-through' }}>
                                {v.voisine}{v.condition?.length ? ` (avec ${v.condition.join(' ou ')})` : ''}
                              </span>
                              <span className="ats-mono" style={{ fontWeight: 500 }}>{Math.round((v.actif ? v.taux_effectif : v.taux) * 100)}&nbsp;%</span>
                              {v.etat === 'proposee' && <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>proposée</span>}
                              {(v.etat === 'retenue_besoin' || v.etat === 'refusee_besoin')
                                ? <button type="button" disabled={occupe} onClick={() => trancherVoisine(v, null)}
                                    title="Annuler la décision prise pour ce besoin" aria-label={`Annuler la décision sur ${v.voisine}`}
                                    style={{ ...btnGhost, padding: '0 4px', fontSize: '11px' }}><BI icon={occupe ? 'arrow-repeat' : 'arrow-counterclockwise'} /></button>
                                : <>
                                    {v.etat === 'proposee' && (
                                      <button type="button" disabled={occupe} onClick={() => trancherVoisine(v, 'oui')}
                                        title="Elle compte pour ce besoin, à son taux entier" aria-label={`${v.voisine} compte pour ce besoin`}
                                        style={{ ...btnGhost, padding: '0 4px', fontSize: '11px' }}><BI icon={occupe ? 'arrow-repeat' : 'check2'} /></button>
                                    )}
                                    <button type="button" disabled={occupe} onClick={() => trancherVoisine(v, 'non')}
                                      title="Elle ne compte pas pour ce besoin" aria-label={`${v.voisine} ne compte pas pour ce besoin`}
                                      style={{ ...btnGhost, padding: '0 4px', fontSize: '11px' }}><BI icon="x-lg" /></button>
                                    {v.etat === 'proposee' && (
                                      <button type="button" disabled={occupe} onClick={() => trancherVoisine(v, 'partout')}
                                        title="La confirmer pour tous les besoins de l'organisation"
                                        style={{ ...btnGhost, padding: '0 5px', fontSize: '10.5px' }}>Tous les besoins</button>
                                    )}
                                  </>}
                            </span>
                          );
                        })}
                      </span>
                    )}
                    {/* LA PART RÉELLE : ce que cette ligne pèse dans le score,
                        catégorie et niveau confondus. C'est le retour qui
                        empêche de tout mettre à 5. */}
                    {parts && !horsCalcul && (
                      <span title="Ce que cette exigence pèse dans le score, avec les poids et niveaux en vigueur"
                        style={{ marginLeft: '8px', fontSize: '10.5px', fontFamily: 'var(--font-mono, inherit)', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                        ≈ {pct(parts.parLigne[`${cat.cle}|${x.label}`] || 0)}
                      </span>
                    )}
                  </span>
                  {/* Le niveau : un menu court, du plus fort au plus faible,
                      construit sur l'échelle que le serveur publie. Le 5 dit
                      qu'il plafonne, le 0 qu'il retire — dans le menu même. */}
                  {niveaux && (
                    <select value={Number.isInteger(x.niveau) ? x.niveau : ''} disabled={busy === x.label}
                      onChange={e => deplacer(x.label, { niveau: Number(e.target.value) })}
                      aria-label={`Niveau d'importance de « ${x.label} »`}
                      title={niveaux.find(n => n.niveau === x.niveau)?.effet || ''}
                      style={{ flex: 'none', fontSize: '11px', fontFamily: 'inherit', padding: '3px 6px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--panel, #fff)', color: 'var(--text)', fontWeight: x.niveau === plafonne?.niveau ? 700 : 500 }}>
                      {niveaux.map(n => (
                        <option key={n.niveau} value={n.niveau} title={n.effet}>
                          {n.niveau} — {n.libelle}{n.plafonne ? ' (plafonne)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {/* Déplacer : un menu, pas un glisser-déposer — on règle ça
                      une ligne à la fois, au clavier comme à la souris. */}
                  <select value={cat.cle} disabled={busy === x.label}
                    onChange={e => deplacer(x.label, { bucket: e.target.value })}
                    aria-label={`Catégorie de « ${x.label} »`}
                    style={{ flex: 'none', fontSize: '11px', fontFamily: 'inherit', padding: '3px 6px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--panel, #fff)', color: 'var(--text)' }}>
                    {cats.filter(c => c.compte !== false).map(c => <option key={c.cle} value={c.cle}>{c.libelle}</option>)}
                    {/* Retirer du calcul, en bas du menu : c'est une sortie,
                        pas une catégorie de plus. Le serveur décide de son
                        existence (cat.compte === false), l'écran n'invente
                        aucune option. */}
                    {cats.some(c => c.compte === false) && (
                      <optgroup label="Hors calcul">
                        {cats.filter(c => c.compte === false).map(c => <option key={c.cle} value={c.cle}>{c.libelle}</option>)}
                      </optgroup>
                    )}
                  </select>
                  {(x.corrige || x.niveau_regle) && (
                    <button type="button" onClick={() => deplacer(x.label, { bucket: null })} disabled={busy === x.label}
                      title="Annuler les réglages de cette ligne (catégorie et niveau) et reprendre la lecture automatique"
                      style={{ ...btnGhost, flex: 'none', padding: '3px 7px', fontSize: '11px' }}><BI icon="arrow-counterclockwise" /></button>
                  )}
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Popup de pré-lancement d'un matching (AO ou besoin/JD) : garder les réglages
 * par défaut de l'organisation, ou ajuster pour CE lancement uniquement.
 *
 * onLancer(null) = réglages par défaut ; onLancer({cle: poids}) = ajustés.
 * Catalogue absent : on peut toujours lancer — avec l'algorithme actuel.
 */
export function MatchingLancementModal({ titre, sousTitre, confirmLabel = 'Lancer le matching', besoinId = null, onLancer, onClose }) {
  const [cat, setCat] = useState(null);        // null = chargement | {criteres|absent|erreur}
  const [mode, setMode] = useState('defaut');  // defaut | ajuste
  const [poids, setPoids] = useState({});

  useEffect(() => {
    let annule = false;
    (async () => {
      const res = await chargerCatalogueMatching();
      if (annule) return;
      setCat(res);
      if (res.criteres) setPoids(poidsDe(res.criteres));
    })();
    return () => { annule = true; };
  }, []);

  const lancer = () => {
    const total = Object.values(poids).reduce((s, v) => s + (Number(v) || 0), 0);
    if (mode === 'ajuste' && total === 0) return;
    onLancer(mode === 'ajuste' && cat?.criteres ? poids : null);
    onClose();
  };

  const radio = (val, titreR, detail) => (
    <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '11px 13px', border: `1px solid ${mode === val ? 'var(--brand)' : 'var(--border)'}`, borderRadius: '10px', cursor: 'pointer', background: mode === val ? 'rgba(226,35,26,0.04)' : 'var(--panel, #fff)' }}>
      <input type="radio" name="mr-mode" checked={mode === val} onChange={() => setMode(val)} style={{ marginTop: '2px', accentColor: 'var(--brand)' }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{titreR}</span>
        <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>{detail}</span>
      </span>
    </label>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 900, padding: '24px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel, #fff)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '640px', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 16px', borderBottom: '1px solid var(--hair)', flexShrink: 0 }}>
          <BI icon="intersect" style={{ color: 'var(--brand)' }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>{titre || 'Réglages du matching'}</span>
            {sousTitre && <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-2)' }}>{sousTitre}</span>}
          </span>
          <button onClick={onClose} style={btnGhost} title="Fermer"><BI icon="x-lg" /></button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {cat == null && <div style={{ textAlign: 'center', color: 'var(--text-faint)', padding: '18px 0' }}><BI icon="arrow-repeat" style={{ animation: 'mr-spin .7s linear infinite', fontSize: '18px' }} /><style>{`@keyframes mr-spin{to{transform:rotate(360deg)}}`}</style></div>}

          {cat && !cat.criteres && (
            <div style={{ padding: '11px 13px', borderRadius: '10px', background: 'var(--chip-bg)', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.55 }}>
              {cat.absent
                ? 'Le réglage des critères arrive côté serveur — le matching partira avec l\'algorithme actuel.'
                : `Réglages illisibles (${cat.erreur}) — le matching partira avec l'algorithme actuel.`}
            </div>
          )}

          {/* CE QU'ON PONDÈRE, AVANT LES CURSEURS QUI LE PONDÈRENT. Les
              curseurs réglaient quatre catégories dont le contenu ne
              s'affichait nulle part : on pondérait des boîtes fermées, et une
              exigence mal rangée restait invisible (vécu 15.09 : le « Must
              Have » d'un client classé en « important »). */}
          {besoinId != null && (
            <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '13px' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '8px' }}>
                Ce qui est exigé sur ce poste
              </div>
              {/* Les poids que la liste rejoue : ceux qu'on ajuste, ou ceux
                  de l'organisation — jamais des curseurs bougés puis
                  abandonnés en repassant sur « par défaut ». */}
              <ExigencesBesoin besoinId={besoinId}
                poidsCat={cat?.criteres && cat.valeursConnues !== false ? (mode === 'ajuste' ? poids : poidsDefautDe(cat.criteres)) : null} />
            </div>
          )}

          {cat?.criteres && (
            <>
              {radio('defaut', 'Utiliser les réglages par défaut',
                'Ceux de la page Configuration — les mêmes règles pour tous les matchings.')}
              {cat.valeursConnues === false ? (
                <div style={{ padding: '10px 13px', borderRadius: '10px', background: 'var(--chip-bg)', fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                  L'ajustement des poids sera possible dès que le serveur publiera leurs valeurs
                  actuelles — en attendant, le matching part avec les réglages par défaut.
                </div>
              ) : (
                <>
                  {radio('ajuste', 'Ajuster pour ce lancement uniquement',
                    'Les curseurs ci-dessous ne valent que pour ce matching : les réglages par défaut ne bougent pas.')}
                  {mode === 'ajuste' && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '13px' }}>
                      <PoidsSliders criteres={cat.criteres} poids={poids} onChange={setPoids} niveaux={cat.niveaux} />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 16px', borderTop: '1px solid var(--hair)', flexShrink: 0 }}>
          <button onClick={onClose} style={btnGhost}>Annuler</button>
          <button onClick={lancer} disabled={cat == null} style={{ ...btnPlein, opacity: cat == null ? 0.55 : 1 }}>
            <BI icon="intersect" /> {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Bandeau « réglages appliqués » d'un rapport de matching.
 *
 * Le calcul d'un AO démarre souvent tout seul (après l'analyse), sans que
 * personne n'ait choisi de réglages : ce bandeau dit avec QUOI le rapport
 * affiché a été calculé — les poids renvoyés par le serveur quand il les
 * donne, sinon les défauts de l'organisation — et offre le geste d'à côté :
 * « Ajuster et recalculer ».
 */
export function ReglagesMatchingBanner({ ponderations, auteur, date, onAjuster }) {
  const [ouvert, setOuvert] = useState(false);
  const [cat, setCat] = useState(null);
  useEffect(() => {
    if (!ouvert || cat != null) return;
    let annule = false;
    chargerCatalogueMatching().then(res => { if (!annule) setCat(res); });
    return () => { annule = true; };
  }, [ouvert, cat]);

  const libelleDe = (cle) => (cat?.criteres || []).find(c => c.cle === cle)?.libelle || cle;
  // Les poids à montrer : ceux du calcul si le serveur les donne, sinon les
  // poids courants du catalogue (= les défauts qui ont servi).
  const poidsAffiches = ponderations && typeof ponderations === 'object'
    ? Object.entries(ponderations)
    : (cat?.criteres || []).map(c => [c.cle, c.poids ?? c.poids_defaut]);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '9px 12px', marginBottom: '12px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <BI icon="sliders" style={{ color: 'var(--text-3)', flex: 'none' }} />
        <span style={{ fontSize: '12px', color: 'var(--text-2)', flex: 1, minWidth: '200px' }}>
          {ponderations ? (
            <>
              <span style={{ fontWeight: 700, color: '#b45309', background: 'rgba(217,119,6,0.12)', padding: '1px 7px', borderRadius: '5px', marginRight: '6px' }}>Réglages ajustés</span>
              Calculé avec des poids ajustés pour ce lancement
              {auteur ? ` par ${auteur}` : ''}{date ? ` le ${new Date(date).toLocaleDateString('fr-CH')}` : ''} — pas ceux par défaut.
            </>
          ) : 'Calculé avec les réglages par défaut de l\'organisation (lancé automatiquement après l\'analyse).'}
        </span>
        <button onClick={() => setOuvert(v => !v)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
          {ouvert ? 'Masquer les réglages' : 'Voir les réglages'}
        </button>
        {onAjuster && (
          <button onClick={onAjuster} style={{ ...btnGhost, padding: '5px 10px', fontSize: '11.5px' }}>
            <BI icon="sliders" /> Ajuster et recalculer
          </button>
        )}
      </div>
      {ouvert && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '9px' }}>
          {cat == null && <span style={{ fontSize: '11.5px', color: 'var(--text-faint)' }}>Chargement…</span>}
          {cat && !cat.criteres && !ponderations && (
            <span style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>
              {cat.absent ? 'Le détail des réglages arrive côté serveur.' : `Réglages illisibles : ${cat.erreur}`}
            </span>
          )}
          {poidsAffiches.map(([cle, v]) => (
            <span key={cle} style={{ fontSize: '11px', fontWeight: 600, padding: '2px 9px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-2)' }}>
              {libelleDe(cle)} <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{v}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * « Pourquoi ce score ? » — le détail d'un score, critère par critère.
 * GET /matching/score-detail?candidate_id=&besoin_id= → (utils/matchingConfig.expliquerScore)
 *   { score, resume, statut_evaluation, motif_non_evaluable,
 *     criteres: [{ critere, categorie, categorie_libelle, statut: 'evalue' | 'a_verifier',
 *                  note (0..1), poids_categorie_pct, poids_interne,
 *                  contribution_pts, contribution_max_pts, preuve, explication }],
 *     formule: { texte, base_criteres_pts, role_fit, role_fit_raison, niveau_fit, niveau_ecart, niveau_ecart_detail, bonus_pts, caps[], score_avant_cap,
 *                exercice_candidat?, exercice_source?, exercice_reason? },
 *     ponderations: { preset, parts_effectives_pct, personnalisees, source } }
 * Vécu 15.09 : le modal lisait `libelle`/`poids`/`contribution` et une note sur
 * 100 — un contrat jamais servi — et n'affichait que la phrase d'explication.
 * Il lit désormais la forme réelle (l'ancienne reste comprise en repli).
 */
const normaliserCritere = (c) => {
  // Ancien contrat (jamais servi, gardé par prudence) : note sur 100, libelle/cle.
  const ancien = c.critere == null && (c.libelle != null || c.cle != null);
  const note = c.note == null ? null : Number(c.note);
  return {
    nom: c.critere || c.libelle || c.cle || '',
    categorie: c.categorie || null,
    categorie_libelle: c.categorie_libelle || null,
    statut: c.statut || 'evalue',
    pct: note == null || Number.isNaN(note) ? null : Math.max(0, Math.min(100, ancien ? note : note * 100)),
    poids_pct: c.poids_categorie_pct ?? c.poids ?? null,
    // Catégorie mise à 0 dans les réglages : elle ne compte pas. Ce n'est pas
    // « 0 point obtenu » — sans cette distinction, un client qui se moque des
    // compétences optionnelles lisait quand même un manque du candidat.
    ignoree: c.categorie_ignoree === true,
    apporte: c.contribution_pts ?? c.contribution ?? null,
    max: c.contribution_max_pts ?? null,
    preuve: c.preuve || null,
    explication: c.explication || null,
  };
};
const couleurNote = (pct) => (pct == null ? 'var(--erp-text-tertiary)' : pct >= 75 ? 'var(--erp-success)' : pct >= 50 ? 'var(--erp-warning)' : 'var(--erp-danger)');
const pts = (v) => (v == null ? '—' : String(Math.round(Number(v) * 10) / 10));
// `chemin` — d'où vient la décomposition. Par défaut le couple candidat ×
// besoin ; un PROFIL PROPOSÉ PAR UN SOURCEUR n'a pas encore de fiche
// candidat (demande 22.09 : « ce menu, on ne l'a pas sur cet écran »), son
// évaluation vit sur la proposition. Une seule modale, un seul rendu : deux
// écrans qui décomposeraient un score chacun à leur façon finiraient par
// répondre deux choses à la même question.
export function ScoreDetailModal({ candidateId, besoinId, nom, poste, chemin = null, onClose }) {
  const [etat, setEtat] = useState('chargement'); // chargement | pret | absent | erreur
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ candidate_id: candidateId, besoin_id: besoinId });
        const url = chemin || `${BASE}/matching/score-detail?${qs.toString()}`;
        const r = await fetch(url, { headers: authH() });
        // 404/409/422 portent une PHRASE qui dit quoi faire (re-scorer, lancer
        // un matching, ou pourquoi ce profil n'a pas de score) : on l'affiche,
        // au lieu de l'état vide générique qui laisse chercher.
        const j = await r.json().catch(() => ({}));
        if (!r.ok && !j.error) { if (!annule) setEtat('absent'); return; }
        if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
        if (!annule) { setDetail(j.data || j); setEtat('pret'); }
      } catch (e) { if (!annule) { setDetail({ erreur: e.message }); setEtat('erreur'); } }
    })();
    return () => { annule = true; };
  }, [candidateId, besoinId, chemin]);

  const criteres = detail?.criteres || [];
  const score = detail?.score;
  // Les critères GROUPÉS par catégorie (primordiales, importantes…), dans
  // l'ordre du serveur, avec le poids de la catégorie et ce qu'elle apporte
  // au total. Sans catégorie (contrat ancien), un seul groupe sans en-tête.
  const groupes = [];
  for (const brut of criteres) {
    const l = normaliserCritere(brut);
    let g = groupes.find(x => x.cle === (l.categorie || ''));
    if (!g) { g = { cle: l.categorie || '', libelle: l.categorie_libelle || null, poids_pct: l.poids_pct, ignoree: l.ignoree === true, lignes: [], apporte: 0, max: 0 }; groupes.push(g); }
    g.lignes.push(l);
    if (l.apporte != null) g.apporte += Number(l.apporte) || 0;
    if (l.max != null) g.max += Number(l.max) || 0;
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 950, padding: '24px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div {...ancre('score-detail')} onClick={e => e.stopPropagation()} style={{ background: 'var(--panel, #fff)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '580px', maxHeight: '84vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 16px', borderBottom: '1px solid var(--hair)', flexShrink: 0 }}>
          <BI icon="intersect" style={{ color: 'var(--brand)' }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>
              Pourquoi ce score ?{score != null ? ` — ${Math.round(Number(score))}/100` : ''}
            </span>
            {/* Le nom et le poste viennent des props quand l'écran les
                connaît, sinon de la réponse elle-même — qui les porte déjà.
                Un en-tête vide sur une modale qu'on vient d'ouvrir fait
                douter qu'on regarde le bon profil. */}
            {(nom || poste || detail?.candidat?.nom || detail?.besoin?.titre) && (
              <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-2)' }}>
                {[nom || detail?.candidat?.nom, poste || detail?.besoin?.titre].filter(Boolean).join(' · ')}
              </span>
            )}
          </span>
          <button onClick={onClose} style={btnGhost} title="Fermer"><BI icon="x-lg" /></button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px' }}>
          {etat === 'chargement' && <div style={{ textAlign: 'center', color: 'var(--text-faint)', padding: '18px 0' }}><BI icon="arrow-repeat" style={{ animation: 'mr-spin .7s linear infinite', fontSize: '18px' }} /><style>{`@keyframes mr-spin{to{transform:rotate(360deg)}}`}</style></div>}

          {etat === 'absent' && (
            <p style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
              Le détail du score arrive côté serveur. Il montrera, critère par critère, ce qui a
              compté (poids × note = contribution) — de quoi expliquer chaque point du score.
            </p>
          )}
          {etat === 'erreur' && (
            <p style={{ fontSize: '12.5px', color: 'var(--text-2)', margin: 0 }}>Détail indisponible : {detail?.erreur}</p>
          )}

          {etat === 'pret' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {detail?.resume && <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>{detail.resume}</p>}
              {/* Réserve de niveau (formule.niveau_fit < 1) : un écart de
                  séniorité à QUALIFIER en entretien — pas un rejet. Champs
                  absents = évaluation d'avant le contrat : rien à afficher. */}
              {detail?.formule?.niveau_fit != null && Number(detail.formule.niveau_fit) < 1 && detail.formule.niveau_ecart && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', border: '1px solid var(--erp-warning)', borderRadius: '8px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)', fontSize: '12px', lineHeight: 1.5 }}>
                  <BI icon="question-circle" style={{ flex: 'none', marginTop: '2px' }} />
                  <span>
                    <strong>Réserve de niveau : {detail.formule.niveau_ecart}.</strong> Un point à qualifier en entretien — ce n'est pas un rejet.
                    {/* Le libellé court tient dans la pastille ; le détail, lui,
                        a la place de s'expliquer ici. */}
                    {detail.formule.niveau_ecart_detail && (
                      <span style={{ display: 'block', marginTop: '5px', fontWeight: 400 }}>{detail.formule.niveau_ecart_detail}</span>
                    )}
                  </span>
                </div>
              )}
              {/* Exercice retenu pour évaluer le niveau, et D'OÙ il vient :
                  une posture lue dans le CV ne se défend pas comme un repli
                  calculé sur les règles du barème — la personne qui présente le
                  profil au client doit pouvoir faire la différence. */}
              {(detail?.formule?.exercice_candidat || detail?.formule?.exercice_reason) && (
                <p style={{ margin: 0, fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                  {detail.formule.exercice_candidat ? <>Exercice retenu : <span style={{ color: 'var(--text-2)' }}>{detail.formule.exercice_candidat}</span></> : null}
                  {detail.formule.exercice_source === 'ia' || detail.formule.exercice_source === 'regles' ? (
                    <span className="erp-chip" style={{ marginLeft: '7px', verticalAlign: 'middle' }}
                      title={detail.formule.exercice_source === 'ia'
                        ? 'La posture est décrite telle quelle dans le CV du candidat'
                        : 'Aucune posture décrite dans le CV : elle a été déduite du barème (années, intitulés)'}>
                      {detail.formule.exercice_source === 'ia' ? 'lu dans le CV' : 'déduit du barème'}
                    </span>
                  ) : null}
                  {detail.formule.exercice_reason ? <span style={{ display: 'block', marginTop: '4px' }}>{detail.formule.exercice_reason}</span> : null}
                </p>
              )}
              {detail?.statut_evaluation && detail.statut_evaluation !== 'evalue' && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', border: '1px solid var(--erp-danger)', borderRadius: '8px', color: 'var(--erp-danger)', fontSize: '12px', lineHeight: 1.5 }}>
                  <BI icon="exclamation-circle" style={{ flex: 'none', marginTop: '2px' }} />
                  <span><strong>Score non évaluable.</strong>{detail.motif_non_evaluable ? ` ${detail.motif_non_evaluable}` : ''}</span>
                </div>
              )}
              {groupes.map((g, gi) => (
                <div key={g.cle || gi} style={{ display: 'flex', flexDirection: 'column', gap: '10px', opacity: g.ignoree ? 0.55 : 1 }}>
                  {/* L'en-tête de catégorie : son poids dans le score, et ce que
                      ses critères apportent ensemble (sur le maximum possible). */}
                  {g.libelle && (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', paddingBottom: '4px', borderBottom: '1px solid var(--hair)' }}>
                      <span style={{ flex: 1, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)' }}>{g.libelle}</span>
                      <span style={{ fontFamily: "var(--erp-font-mono, 'IBM Plex Mono', monospace)", fontSize: '11px', color: g.ignoree ? 'var(--text-faint)' : 'var(--text-3)' }}
                        title={g.ignoree ? 'Cette catégorie est ignorée dans vos réglages de scoring : elle ne compte ni en faveur ni contre le candidat. Ses critères restent affichés pour information.' : undefined}>
                        {g.ignoree
                          ? 'non comptée'
                          : <>{g.poids_pct != null ? `poids ${g.poids_pct} %` : ''}{g.max > 0 ? ` · ${pts(g.apporte)} / ${pts(g.max)} pts` : ''}</>}
                      </span>
                    </div>
                  )}
                  {g.lignes.map((l, i) => {
                    const evalue = l.statut === 'evalue' && l.pct != null;
                    const fg = couleurNote(evalue ? l.pct : null);
                    return (
                      <div key={l.nom || i} title={l.explication || undefined}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', flex: 1, minWidth: 0 }}>{l.nom}</span>
                          {evalue
                            ? <span style={{ fontFamily: "var(--erp-font-mono, 'IBM Plex Mono', monospace)", fontSize: '11.5px', color: fg, whiteSpace: 'nowrap' }}>
                                {Math.round(l.pct)} %{l.apporte != null ? ` · apporte ${pts(l.apporte)}${l.max != null ? ` / ${pts(l.max)}` : ''} pts` : ''}
                              </span>
                            : <span style={{ fontSize: '10.5px', fontWeight: 700, color: 'var(--erp-warning)', border: '1px solid var(--erp-warning)', borderRadius: '6px', padding: '1px 6px', whiteSpace: 'nowrap' }}
                                title={l.explication || 'Ce critère n’a pas pu être évalué : il ne pèse ni pour ni contre.'}>
                                <BI icon="question-circle" style={{ fontSize: '9px' }} /> à vérifier
                              </span>}
                        </div>
                        <div style={{ height: '6px', borderRadius: '4px', background: 'var(--chip-bg)', marginTop: '4px', overflow: 'hidden' }}>
                          {evalue && <div style={{ width: `${l.pct}%`, height: '100%', borderRadius: '4px', background: fg }} />}
                        </div>
                        {/* La preuve lue dans le CV : c'est elle qui justifie la
                            note — ou son absence. Sans preuve, la phrase du serveur. */}
                        {l.preuve
                          ? <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5, fontStyle: 'italic' }}><BI icon="quote" style={{ fontSize: '9px', marginRight: '4px' }} />{l.preuve}</div>
                          : (!evalue && l.explication ? <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>{l.explication}</div> : null)}
                      </div>
                    );
                  })}
                </div>
              ))}
              {!criteres.length && <p style={{ fontSize: '12.5px', color: 'var(--text-3)', margin: 0 }}>Le serveur n'a renvoyé aucun critère pour cette paire.</p>}
              {/* La formule du serveur, telle quelle : somme des contributions ×
                  adéquation de rôle × niveau + bonus, plafonds — c'est ce qui
                  fait le chiffre du titre, et la pondération utilisée. */}
              {(detail?.formule?.texte || detail?.ponderations) && (
                <div style={{ paddingTop: '10px', borderTop: '1px solid var(--hair)', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.6 }}>
                  {detail?.formule?.texte && <div style={{ fontFamily: "var(--erp-font-mono, 'IBM Plex Mono', monospace)", fontSize: '11px', color: 'var(--text-2)' }}>{detail.formule.texte}</div>}
                  {detail?.formule?.role_fit_raison && Number(detail.formule.role_fit) < 1 && <div style={{ marginTop: '4px' }}>Adéquation de rôle {detail.formule.role_fit} : {detail.formule.role_fit_raison}</div>}
                  {detail?.ponderations && (detail.ponderations.preset || detail.ponderations.personnalisees) && (
                    <div style={{ marginTop: '4px' }}>
                      Pondération : {detail.ponderations.preset ? `profil « ${detail.ponderations.preset} »` : 'profil par défaut'}{detail.ponderations.personnalisees ? ' — poids ajustés au lancement' : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Section « Algorithme de matching » de la page Configuration : les poids par
 * défaut de l'organisation. Preuve par relecture après enregistrement — un
 * poids « enregistré » qui ne l'est pas fausserait tous les scores suivants.
 */
export function MatchingConfigSection({ showNotification }) {
  const [cat, setCat] = useState(null);
  const [poids, setPoids] = useState({});
  const [busy, setBusy] = useState(false);
  // Les poids par défaut valent pour toute l'organisation : seul un admin les
  // modifie. Tout le monde les VOIT (c'est la transparence recherchée), et
  // tout le monde garde l'ajustement ponctuel au lancement d'un matching.
  const admin = isAoAdmin(APIService.getCurrentUser());

  const charger = useCallback(async () => {
    const res = await chargerCatalogueMatching();
    setCat(res);
    if (res.criteres) setPoids(poidsDe(res.criteres));
    return res;
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async () => {
    // Ceinture : jamais envoyer un poids que le serveur n'a pas donné.
    if (Object.values(poids).some(v => v == null)) {
      showNotification?.('Certains poids ne sont pas publiés par le serveur — rien n\'a été enregistré.', 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/config/matching`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ poids }) });
      if (r.status === 404 || r.status === 405 || r.status === 501) {
        showNotification?.('L\'enregistrement des poids arrive côté serveur — rien n\'a été modifié.', 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      // Preuve avant annonce : on relit et on compare, poids par poids.
      const relu = await charger();
      const perdus = (relu.criteres || []).filter(c => Number(c.poids ?? c.poids_defaut ?? 0) !== Number(poids[c.cle] ?? 0)).map(c => c.libelle || c.cle);
      if (perdus.length) showNotification?.(`Non conservé par le serveur : ${perdus.join(', ')}`, 'error');
      else showNotification?.('Poids du matching enregistrés — ils s\'appliquent aux prochains matchings.', 'success');
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <p style={{ margin: '0 0 6px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
        Le score d'un candidat sur un besoin est la somme de quelques critères, chacun avec un
        poids. Ici se règlent les poids <b>par défaut</b> de l'organisation — au lancement d'un
        matching, ils peuvent aussi être ajustés pour ce lancement seulement.
      </p>
      <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.6 }}>
        Contrairement à un chiffre donné par un chatbot, chaque point d'un score erpia est
        justifié critère par critère, avec les mêmes règles pour tous les candidats — le détail
        est visible sur chaque score (« Pourquoi ce score ? »).
      </p>

      {cat == null && <div style={{ color: 'var(--text-faint)', fontSize: '12.5px' }}>Chargement…</div>}
      {cat && !cat.criteres && (
        <div style={{ padding: '11px 13px', borderRadius: '10px', background: 'var(--chip-bg)', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.55 }}>
          {cat.absent ? 'Le catalogue des critères arrive côté serveur — rien à régler pour l\'instant.' : `Catalogue illisible : ${cat.erreur}`}
        </div>
      )}

      {cat?.criteres && (
        <>
          {cat.valeursConnues === false && (
            <div style={{ marginBottom: '10px', padding: '11px 13px', borderRadius: '10px', background: 'rgba(217,119,6,0.10)', fontSize: '12px', color: '#b45309', lineHeight: 1.55 }}>
              Le serveur a publié les critères mais pas leurs poids actuels : les valeurs
              s'affichent « — » et l'enregistrement est désactivé — enregistrer des zéros
              inventés casserait tous les scores. Les vraies valeurs apparaîtront dès que le
              serveur les publiera.
            </div>
          )}
          <div style={{ border: '1px solid var(--border)', borderRadius: '11px', padding: '14px' }}>
            <PoidsSliders criteres={cat.criteres} poids={poids} onChange={setPoids} niveaux={cat.niveaux} disabled={!admin || cat.valeursConnues === false} />
          </div>
          {admin && cat.valeursConnues === false ? null : admin ? (
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
              <button onClick={enregistrer} disabled={busy} style={{ ...btnPlein, opacity: busy ? 0.55 : 1 }}>
                <BI icon={busy ? 'arrow-repeat' : 'check2'} style={busy ? { animation: 'mr-spin .8s linear infinite' } : undefined} /> {busy ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button onClick={() => setPoids(poidsDefautDe(cat.criteres))} disabled={busy} style={btnGhost}
                title="Remet les curseurs aux valeurs d'origine — pensez à Enregistrer ensuite.">
                Revenir aux poids d'origine
              </button>
            </div>
          ) : (
            <p style={{ marginTop: '12px', marginBottom: 0, fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.55 }}>
              Ces réglages valent pour toute l'organisation : seul un administrateur peut les
              modifier. Vous pouvez en revanche les ajuster ponctuellement au lancement d'un
              matching (AO ou besoin) — sans toucher aux valeurs par défaut.
            </p>
          )}
        </>
      )}
    </div>
  );
}
